import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { CatalogRoute } from '../../components/CatalogRoute';
import { CatalogAdminRoute } from '../../components/CatalogAdminRoute';

// Catalog (AI Onboarding) — code-split exactly as App.tsx splits every page.
const CatalogConnect = React.lazy(() => import('./CatalogConnect'));
const CatalogLayout = React.lazy(() => import('./CatalogLayout'));
const ReadinessShell = React.lazy(() => import('./ReadinessShell'));
const CatalogPublish = React.lazy(() => import('./CatalogPublish'));
const CatalogTracking = React.lazy(() => import('./CatalogTracking'));
const CatalogProductsGrid = React.lazy(() => import('./products/CatalogProductsGrid'));
const CatalogBrowsePage = React.lazy(() => import('./browse/CatalogBrowsePage'));
const MarketplacesPage = React.lazy(() => import('./marketplaces/MarketplacesPage'));
const ImageReadinessRoute = React.lazy(() =>
  import('./ImageReadinessRoute').then((m) => ({ default: m.ImageReadinessRoute })),
);
const HtsRoute = React.lazy(() => import('./HtsRoute').then((m) => ({ default: m.HtsRoute })));
const MetafieldMappingsRoute = React.lazy(() =>
  import('./MetafieldMappings').then((m) => ({ default: m.MetafieldMappingsRoute })),
);
const ShipmentRoute = React.lazy(() =>
  import('./shipment/ShipmentRoute').then((m) => ({ default: m.ShipmentRoute })),
);
const ShipmentDetailsRoute = React.lazy(() =>
  import('./shipment/ShipmentDetailsRoute').then((m) => ({ default: m.ShipmentDetailsRoute })),
);

/**
 * The catalog's route set, mounted TWICE by `App.tsx` (Ari, 2026-09-10,
 * option 1): under the brand shell at `/catalog` and under the admin shell at
 * `/admin/catalog`, so an admin never leaves their own sidebar to operate it.
 * One definition, so the two mounts cannot drift. Every in-catalog link
 * derives its prefix from `catalogPaths.ts`.
 *
 * Tracking is cross-brand, so it sits outside `CatalogRoute`'s brand gate and
 * outside `CatalogLayout`'s stepper.
 */
export function catalogRoutes(base: string): React.ReactElement {
  return (
    <>
      <Route
        path={base}
        element={
          <CatalogRoute>
            <CatalogBrowsePage />
          </CatalogRoute>
        }
      />
      <Route
        path={`${base}/marketplaces`}
        element={
          <CatalogRoute>
            <MarketplacesPage />
          </CatalogRoute>
        }
      />
      <Route
        path={`${base}/tracking`}
        element={
          <CatalogAdminRoute>
            <CatalogTracking />
          </CatalogAdminRoute>
        }
      />
      <Route
        element={
          <CatalogRoute>
            <CatalogLayout />
          </CatalogRoute>
        }
      >
        <Route path={`${base}/connect`} element={<CatalogConnect />} />
        <Route path={`${base}/products`} element={<CatalogProductsGrid />} />
        <Route path={`${base}/readiness`} element={<ReadinessShell />} />
        <Route path={`${base}/images`} element={<ImageReadinessRoute />} />
        <Route path={`${base}/hts`} element={<HtsRoute />} />
        <Route path={`${base}/metafields`} element={<MetafieldMappingsRoute />} />
        <Route path={`${base}/shipment`} element={<ShipmentRoute />} />
        <Route path={`${base}/shipment/details`} element={<ShipmentDetailsRoute />} />
        <Route path={`${base}/review`} element={<Navigate to={`${base}/readiness`} replace />} />
        <Route path={`${base}/cockpit`} element={<Navigate to={`${base}/readiness`} replace />} />
        <Route path={`${base}/validate`} element={<Navigate to={`${base}/readiness`} replace />} />
        <Route path={`${base}/publish`} element={<CatalogPublish />} />
      </Route>
    </>
  );
}
